import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
  UsageStats,
  Subscription,
  PaginatedDocuments,
  DashboardData
} from '../models/dashboard.models';

/**
 * Dashboard API Service
 * Handles all dashboard-related API calls
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  private isSuspendedError(error: unknown): boolean {
    const err = error as { status?: number; error?: { detail?: { code?: string } | string } };
    if (err?.status !== 403) {
      return false;
    }
    const detail = err.error?.detail;
    if (typeof detail === 'string') {
      return detail.toLowerCase().includes('suspended');
    }
    return detail?.code === 'ORG_SUSPENDED';
  }

  private swallowUnlessSuspended<T>(fallback: T) {
    return catchError((error: unknown) => {
      if (this.isSuspendedError(error)) {
        return throwError(() => error);
      }
      return of(fallback);
    });
  }

  /**
   * Get usage statistics for current billing period
   */
  getUsageStats(): Observable<UsageStats> {
    return this.http.get<UsageStats>(`${this.apiUrl}/billing/usage`);
  }

  /**
   * Get current subscription details
   */
  getSubscription(): Observable<Subscription | null> {
    return this.http.get<Subscription>(`${this.apiUrl}/billing/subscription`).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get recent documents (last 10)
   */
  getRecentDocuments(limit: number = 10): Observable<PaginatedDocuments> {
    return this.http.get<PaginatedDocuments>(
      `${this.apiUrl}/documents?page=1&page_size=${limit}`
    );
  }

  /**
   * Get API keys count
   */
  getAPIKeysCount(): Observable<number> {
    return this.http.get<{ keys: any[], total: number }>(
      `${this.apiUrl}/api-keys?page=1&page_size=1`
    ).pipe(
      map(response => response.total),
      catchError(() => of(0))
    );
  }

  /**
   * Get all dashboard data in a single call
   * Uses forkJoin to make parallel requests
   */
  getDashboardData(): Observable<DashboardData> {
    // Skip loading indicator and error toasts for optional dashboard billing calls
    const headers = new HttpHeaders()
      .set('X-Skip-Loading', 'true')
      .set('X-Skip-Error-Notification', 'true');

    return forkJoin({
      usage: this.http.get<UsageStats>(`${this.apiUrl}/billing/usage`, { headers }).pipe(
        this.swallowUnlessSuspended<UsageStats | null>(null)
      ),
      subscription: this.http.get<Subscription>(`${this.apiUrl}/billing/subscription`, { headers }).pipe(
        this.swallowUnlessSuspended<Subscription | null>(null)
      ),
      documents: this.http.get<PaginatedDocuments>(
        `${this.apiUrl}/documents?page=1&page_size=10`,
        { headers }
      ).pipe(
        this.swallowUnlessSuspended<PaginatedDocuments>({
          items: [], total: 0, page: 1, page_size: 10, total_pages: 0, has_next: false, has_prev: false
        })
      ),
      apiKeysCount: this.http.get<{ keys: any[], total: number }>(
        `${this.apiUrl}/api-keys?page=1&page_size=1`,
        { headers }
      ).pipe(
        map(response => response.total),
        this.swallowUnlessSuspended<number>(0)
      )
    }).pipe(
      map((result) => {
        const data = result as {
          usage: UsageStats | null;
          subscription: Subscription | null;
          documents: PaginatedDocuments;
          apiKeysCount: number;
        };
        return {
          usage: data.usage,
          subscription: data.subscription,
          recentDocuments: data.documents.items,
          documentsTotal: data.documents.total,
          apiKeysCount: data.apiKeysCount,
          loading: false,
          error: null,
          lastUpdated: new Date()
        };
      }),
      catchError(error => {
        console.error('Error loading dashboard data:', error);
        return of({
          usage: null,
          subscription: null,
          recentDocuments: [],
          documentsTotal: 0,
          apiKeysCount: 0,
          loading: false,
          error: 'Failed to load dashboard data',
          lastUpdated: null
        });
      })
    );
  }

  /**
   * Get usage chart data from analytics API
   */
  getUsageChartData(days: number = 30): Observable<Array<{
    date: string;
    documents: number;
    pages: number;
    credits: number;
    successful: number;
    failed: number;
    average_processing_time: number;
  }>> {
    const dateRange = days <= 7 ? 'last7' : days <= 30 ? 'last30' : 'last90';
    const headers = new HttpHeaders().set('X-Skip-Error-Notification', 'true');
    return this.http.get<{ date: string; documents: number; pages?: number; credits?: number; successful?: number; failed?: number; average_processing_time?: number }[]>(
      `${this.apiUrl}/analytics/daily-usage`,
      { params: { date_range: dateRange }, headers }
    ).pipe(
      map((rows) => (rows || []).map((row) => ({
        date: row.date,
        documents: row.documents ?? 0,
        pages: row.pages ?? 0,
        credits: row.credits ?? 0,
        successful: row.successful ?? 0,
        failed: row.failed ?? 0,
        average_processing_time: row.average_processing_time ?? 0,
      }))),
      catchError(() => of([]))
    );
  }

  /**
   * @deprecated Use getUsageChartData which calls the analytics API
   */
  private generateDateLabels(days: number): string[] {
    const labels: string[] = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
    
    return labels;
  }
}

